/**
 * R30 V1 — the §40.4b `composer-draft` client-message wire shape + debounce lifecycle.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * Field session (dev-117): the visitor was typing her email on the S7 close card when the flow
 * "said done" mid-entry and rerouted to the composer with the screen glitching. Root cause split:
 *   - the server had no idea a value was being typed, so its send/wind-down timeout could fire while
 *     the field was mid-entry (concierge R50 §40.4·b added the server-side timeout-HOLD half);
 *   - the client never emitted the in-progress draft, so Gigi could not confirm the typed-but-
 *     uncommitted value without a re-ask (the §40.4·b client half, parked for a ui round).
 *
 * This module is that client half: while the visitor types in the S7 email/phone fields OR the
 * Type composer, the kiosk emits a DEBOUNCED `composer-draft` activity signal. The signal HOLDS the
 * server's pending timeout (so the flow can't "say done" mid-typing) and surfaces the typed value so
 * Gigi can confirm it. It stops on blur/submit/empty.
 *
 * ── The accepted wire shape (concierge §40.4b, `voice-capture-contract.md`) ─────────────────────
 * `sendClientMessage(type, data)` serialises to `{ type, data }`, so the message is:
 *   { "type": "composer-draft", "data": { "field": "email"|"phone"|"composer", "value": <string> } }
 * Debounced (~1.2s idle), run_llm=false (a context signal, not a turn). The server surfaces the
 * typed-but-uncommitted value so Gigi points at Send/Done instead of RE-ASKing.
 *
 * ⚠️ BINDING STATUS (R30): concierge R56 owns the FINALIZED §40.4b shape + the server consumer, and
 * had NOT landed them at R30 authoring time (their worktree was at `main`). This builds to the
 * DOCUMENTED shape (`voice-capture-contract.md` §40.4·b·client-half — `{field, value}`, debounced,
 * run_llm=false). The binding is UNVERIFIED against a captured fixture — cross-verify + freeze the
 * exact field enum (does it accept `"composer"` for the Type box, or only `email|phone`?) at
 * integration. `COMPOSER_DRAFT_MSG_TYPE` + `buildComposerDraftData` are the single seam if it drifts.
 *
 * Pure (no React / no DOM / no timers of its own beyond the injectable scheduler) so the debounce +
 * wire shape are unit-testable without a WS or jsdom (the kiosk suite has no jsdom).
 */

/** The RTVI client-message type for a live composer/field draft (§40.4b). */
export const COMPOSER_DRAFT_MSG_TYPE = 'composer-draft' as const;

/**
 * The field a draft belongs to. `email`/`phone` are the S7 close-card fields; `composer` is the
 * always-open Type box (an out-of-flow email/phone can be typed there — §29.2 / K2). The server may
 * only care about `email`/`phone` at freeze time; `composer` is carried so the server CAN ground a
 * value typed in the box if it chooses (⚑ confirm the accepted enum at integration).
 */
export type ComposerDraftField = 'email' | 'phone' | 'composer';

/** The `data` payload for a `composer-draft` message: `{ field, value }` (value verbatim, trimmed). */
export function buildComposerDraftData(
  field: ComposerDraftField,
  value: string
): { field: ComposerDraftField; value: string } {
  return { field, value };
}

/**
 * Build the FULL wire message a `sendClientMessage('composer-draft', data)` produces — asserted by
 * the tests to prove the kiosk emits the documented §40.4b shape verbatim. Canonical shape lives here
 * next to the type + data builder (mirrors `userText.buildUserTextMessage`).
 */
export function buildComposerDraftMessage(
  field: ComposerDraftField,
  value: string
): { type: typeof COMPOSER_DRAFT_MSG_TYPE; data: { field: ComposerDraftField; value: string } } {
  return { type: COMPOSER_DRAFT_MSG_TYPE, data: buildComposerDraftData(field, value) };
}

/** Default idle window before a draft is emitted. ~1.2s — long enough to coalesce keystrokes into
 *  one settled emit, short enough that the server's pending-timeout HOLD is refreshed well within its
 *  window (concierge §40.4·b arms a 45s send-pending safety timeout). */
export const COMPOSER_DRAFT_DEBOUNCE_MS = 1_200;

/** A minimal scheduler seam so the emitter is testable with fake timers / a manual clock. */
export type DraftScheduler = {
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (h: ReturnType<typeof setTimeout>) => void;
};

/** The transport seam: how a settled draft reaches the wire (a `sendClientMessage` wrapper). */
export type DraftEmit = (field: ComposerDraftField, value: string) => void;

/**
 * A debounced composer-draft emitter. Each keystroke calls `change(field, value)`; after
 * `debounceMs` of idle the value is emitted ONCE via `emit`. Blur/submit/empty call `stop()` which
 * cancels any pending emit (an empty value is never sent — the hard rule: never emit an empty draft).
 *
 * Contract:
 *   - `change(field, '')` / a whitespace-only value cancels the pending emit and does NOT emit (the
 *     server reads an empty draft as "field cleared" — but we express that only via `stop`, not a
 *     spurious empty emit that could be read as a re-ask trigger).
 *   - a DIFFERENT field arriving before the debounce elapses flushes to the newest field only
 *     (switching focus email→phone emits the phone, not a stale email) — coalesce, last-writer-wins.
 *   - `stop()` cancels any pending emit (blur/submit). Idempotent.
 *   - `dispose()` = `stop()` (unmount cleanup); after dispose, `change` still schedules (the caller
 *     owns lifecycle) — callers should stop scheduling before dispose in practice.
 *
 * Returns a small handle; keep it in a ref across renders. Pure aside from the injected scheduler.
 */
export function createComposerDraftEmitter(
  emit: DraftEmit,
  opts?: { debounceMs?: number; scheduler?: DraftScheduler }
): {
  change: (field: ComposerDraftField, value: string) => void;
  stop: () => void;
  dispose: () => void;
} {
  const debounceMs = opts?.debounceMs ?? COMPOSER_DRAFT_DEBOUNCE_MS;
  const scheduler: DraftScheduler = opts?.scheduler ?? {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (h) => clearTimeout(h),
  };
  let timer: ReturnType<typeof setTimeout> | null = null;

  function cancel(): void {
    if (timer !== null) {
      scheduler.clearTimeout(timer);
      timer = null;
    }
  }

  function change(field: ComposerDraftField, value: string): void {
    // Always cancel the prior pending emit — a new keystroke (or a field switch) re-opens the window.
    cancel();
    // Never emit an empty/whitespace-only draft (hard rule). An empty field is expressed by `stop`.
    if (value.trim() === '') return;
    timer = scheduler.setTimeout(() => {
      timer = null;
      emit(field, value);
    }, debounceMs);
  }

  function stop(): void {
    cancel();
  }

  return { change, stop, dispose: stop };
}
